/**
 * A gardener's preferences on their account: theme, language, text size,
 * hemisphere, unit, how far Coming up looks, automatic stages and what
 * counts as the season for the season review (seasonReviewBy, audit A72)
 * (backend routes/user-settings).
 *
 * Kept on the account rather than the device so a phone and a laptop agree;
 * the few things that are about one device alone live in localPref instead.
 *
 * Endpoints: GET /user-settings/preferences → { preferences }, and PUT the
 * same path with every preference → { preferences } as kept. Reads asked for
 * while one is on its way share it (B24: one Today load used to read the
 * preferences three times, toward the server's per-address limit).
 *
 * Who uses it: shared/context/UserConfigProvider only, which keeps them in
 * its own store (userConfigStore) rather than React Query, and saves the
 * whole set on each change (the season review's choice through its
 * savePreferences). Auth: the signed-in person's own; personal, so
 * X-Grow-Garden does not change them.
 *
 * @module api/preferences
 */

import { apiClient } from './apiClient';
import type { Preferences } from '../context/userConfigStore';

/**
 * The read on its way and the account it was asked for, shared by every
 * caller asking for that same account until it answers; null when none is.
 * Keyed on the account so a read started for one gardener is never handed to
 * the next one signed in on the same device.
 */
let reading: { account: number; promise: Promise<Preferences> } | null = null;

/**
 * The signed-in gardener's preferences, as saved. A read for the same
 * account already on its way is shared rather than asked for again.
 *
 * @param account - The Grow account's id (mysqlUser.user_id) the read is for.
 * @returns Their preferences.
 */
export const getUserPreferences = (account: number): Promise<Preferences> => {
  if (reading && reading.account === account) return reading.promise;
  const promise: Promise<Preferences> = apiClient
    .get('/user-settings/preferences')
    .then((response) => response.data.preferences as Preferences)
    .finally(() => { if (reading?.promise === promise) reading = null; });
  reading = { account, promise };
  return promise;
};

/**
 * Saves the gardener's preferences, whole.
 *
 * @param preferences - Every preference, changed and unchanged alike.
 * @returns The preferences as the server kept them.
 */
export const updateUserPreferences = async (preferences: Preferences): Promise<Preferences> => {
  const response = await apiClient.put('/user-settings/preferences', preferences);
  return response.data.preferences;
};
